import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import { useEffect, useState } from 'react';
import { getFamilyDashboard, sendFamilyOtp, verifyFamilyOtp, addRemoveFamilyMember } from '../../redux-store/actions/investorProfile';
import { GetFamilyDashboardRes, FamilyDashboardResObj, VerifyFamilyOtpRes, SendFamilyOtpRes } from '../../redux-store/types/investorProfile';
import { Box, Button, Typography, useTheme, Divider, Dialog, DialogContent } from '@mui/material';
import { useHistory } from 'react-router-dom';
import BreadCrum from '../../lib/BreadCrum';
import { KeyValue } from '../ApplicationDetails/personalDetails';
import OTPForm from '../../lib/formik/OtpForm';
import { clearFamilyDetails } from '../../redux-store/actions/id';

export default function LeaveFamily(): JSX.Element {
    const theme = useTheme();
    const history = useHistory();
    const dispatch = useDispatch();

    const { pathname: pathName } = history.location;
    const initialRouter = pathName.split('/').filter((item) => item !== '');
    const [otpDialogOpen, setOtpDialogOpen] = useState<boolean>(false);
    const [otpMessage, setOtpMessage] = useState<string>("");
    const [otpReference, setOtpReference] = useState<number>(0);
    const [isSendingOtp, setIsSendingOtp] = useState<boolean>(false);
    const [isVerifyingOtp, setIsVerifyingOtp] = useState<boolean>(false);
    const [isLeavingFamily, setIsLeavingFamily] = useState<boolean>(false);
    const { familyId, familyName, iwappRmId, investorClientId, isCustFamilyHead } = useSelector((state: RootStateType) => state.id);
    const [familyDashboardData, setFamilyDashboardData] = useState<GetFamilyDashboardRes>();
    const [headOfFamily, setHeadOfFamily] = useState<FamilyDashboardResObj>();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const investorRouterPath = [
        { name: 'Dashboard', path: '/investor-dashboard' },
        { name: `${initialRouter[1]}`, path: `/${initialRouter[0]}/${initialRouter[1]}` },
    ];


    const fetchFamilyDashboard = async () => {
        setIsLoading(true);
        try {
            const response = await dispatch(getFamilyDashboard({
                rmId: `${iwappRmId}`,
                familyId: `${familyId}`,
                level: 'All'
            }) as unknown as Promise<GetFamilyDashboardRes>);

            if (response?.isSuccess) {
                setFamilyDashboardData(response);
                const head = response.result?.find((member: FamilyDashboardResObj) => member.isFamilyHead === 'Y');
                setHeadOfFamily(head);
            }
        } catch (error) {
            console.error('Error fetching family dashboard:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchFamilyDashboard();
    }, []);

    const handleCloseOtpDialog = () => {
        setOtpDialogOpen(false);
    };

    const handleSubmit = async (otp: string) => {

        setIsVerifyingOtp(true);
        try {
            const verifyResponse = await dispatch(verifyFamilyOtp({
                otp,
                otpReference
            }) as unknown as Promise<VerifyFamilyOtpRes>);

            if (verifyResponse?.isSuccess) {
                setIsLeavingFamily(true);
                const removeResponse = await dispatch(addRemoveFamilyMember({
                    action: 'REMOVE',
                    familyId: `${familyId}`,
                    familyMembers: [
                        {
                            clientCode: `${investorClientId}`,
                            otpReference: otpReference,
                            isHeadOfFamily: isCustFamilyHead === 'Y'
                        }
                    ]
                })) as unknown as { isSuccess: boolean; message: string };

                if (removeResponse?.isSuccess) {
                    dispatch(clearFamilyDetails());
                    handleCloseOtpDialog();
                    history.push('/investor-family/createFamily');
                }
            }
        } catch (error) {
            console.error('Error verifying OTP or leaving family:', error);
        } finally {
            setIsVerifyingOtp(false);
            setIsLeavingFamily(false);
        }
    };

    const handleResendOtp = async () => {
        await handleLeaveFamilyClick();
    };

    const handleLeaveFamilyClick = async () => {

        setIsSendingOtp(true);
        try {
            const response = await dispatch(sendFamilyOtp({
                clientCode: `${investorClientId}`,
                action: 'REMOVE',
                isHeadOfFamily: isCustFamilyHead === 'Y',
                familyName: familyName,
                hofName: headOfFamily?.customerName ?? "",
            }) as unknown as Promise<SendFamilyOtpRes>);

            if (response?.isSuccess) {
                setOtpReference(Number(response.otpReference));
                setOtpMessage(response.message);
                setOtpDialogOpen(true);
            }
        } catch (error) {
            console.error('Error sending OTP:', error);
        } finally {
            setIsSendingOtp(false);
        }
    };



    return (
        <>
            <BreadCrum array={investorRouterPath} />

            <Box mb={3} mt={2}>
                <Typography variant="h1" color="text.primary" mb={1}>
                    View Family
                </Typography>
            </Box>

            <Box
                sx={{
                    bgcolor: "background.paper",
                    boxShadow: `0px 8px 32px 0px ${theme.palette.common.black}0D`,
                    borderRadius: '16px',
                    p: { xs: 3, sm: 4 },
                }}
            >
                {/* Header Row: Family Info Left + Leave Button Right */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, gap: 3 }}>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: { md: 'center' } }}>
                        <KeyValue title="Family Code" description={familyId || "-"} />
                        <KeyValue title="Family Name" description={familyName || "-"} />
                    </Box>
                    <Button
                        variant="outlined"
                        sx={{
                            px: 2,
                            py: 1,
                            borderRadius: '10px',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            color: theme.palette.primary[500],
                            '&:hover': {
                                backgroundColor: theme.palette.primary[100],
                            },
                        }}
                        onClick={handleLeaveFamilyClick}
                        disabled={isSendingOtp}
                    >
                        {isSendingOtp ? 'Sending OTP...' : 'Leave Family'}
                    </Button>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Head of Family Section */}
                <Box>
                    <Typography variant="body_600" mb={3} color="text.primary">
                        Head of Family Details
                    </Typography>

                    {isLoading ? (
                        <Typography variant="body_500">Loading...</Typography>
                    ) : headOfFamily ? (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
                                gap: 3,
                                alignItems: 'end',
                            }}
                        >
                            <KeyValue title="Name" description={headOfFamily?.customerName ?? "-"} />
                            <KeyValue title="Investor Code" description={headOfFamily?.customer ?? "-"} />
                            <KeyValue title="Phone Number" description={headOfFamily?.phoneNo ?? "-"} />
                            <KeyValue title="Email" description={headOfFamily?.emailId ?? "-"} />
                        </Box>
                    ) : (
                        <Typography variant="body_500">No head of family data available</Typography>
                    )}
                </Box>
            </Box>

            {/* OTP Dialog */}
            <Dialog
                open={otpDialogOpen}
                onClose={handleCloseOtpDialog}
                sx={{
                    '& .MuiPaper-root': {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: 530,
                        maxWidth: '100%',
                        padding: '1.5rem',
                        borderRadius: '12px',
                        background: 'background.paper',
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    },
                }}
            >
                <DialogContent sx={{ width: '100%' }}>
                    <OTPForm
                        onSubmit={handleSubmit}
                        onResend={handleResendOtp}
                        otpMessage={otpMessage}
                        warningMessage=""
                    />
                </DialogContent>
            </Dialog>
        </>
    );
}